import React from "react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { COMPANY, MVV, TEAM, WHY_CHOOSE } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";
import { Sprout, Leaf, Recycle, Droplets, Sparkles, Cpu, Globe2 } from "lucide-react";

const iconMap = { Sprout, Leaf, Recycle, Droplets, Sparkles, Cpu, Globe2 };

const About = () => (
  <>
    <SEO
      title="About — Rudrash Private Limited"
      description="Rudrash Private Limited is a newly incorporated agribusiness based in Kolkata, specializing in controlled-environment cultivation of mushrooms, saffron and poultry."
    />
    <div data-testid="page-about">
      <PageHero
        eyebrow="About Rudrash"
        title="A new chapter for eastern Indian agriculture."
        subtitle={`${COMPANY.name} was founded in ${COMPANY.founded} with a singular obsession: bring controlled-environment, soilless farming into the heart of eastern India — building resilient, high-value supply chains for mushrooms, saffron and poultry.`}
        image="https://images.pexels.com/photos/20075896/pexels-photo-20075896.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
      />

      {/* Story */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-6 md:px-10 xl:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <FadeUp className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="eyebrow text-forest">Our Story</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-forest leading-[1.05]">
              Inspired by the future of aeroponics.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7 space-y-6 text-lg text-muted-ink leading-relaxed">
            <p>
              Rudrash was born out of a conviction that Indian agriculture must be reinvented for a warmer, more urban future. Inspired by breakthroughs in aeroponic and soilless farming worldwide, our team set out to localise these technologies for the climate, crops and markets of eastern India.
            </p>
            <p>
              From our Kolkata base, we are constructing integrated facilities that combine mushroom cultivation, aeroponic saffron chambers, and free-range poultry into a single resilient operation — designed to deliver fresh, traceable and chemical-free produce to chefs, retailers, and export partners.
            </p>
            <p>
              Every decision we take is filtered through five values: <em className="italic text-forest not-italic font-semibold">Innovation, Sustainability, Quality, Integrity, Community.</em>
            </p>

            <div className="mt-10 border-l-2 border-saffron pl-6 py-2">
              <p className="eyebrow text-muted-ink">Corporate Identity</p>
              <p className="mt-2 font-serif text-2xl text-forest" data-testid="about-cin">
                CIN · {COMPANY.cin}
              </p>
              <p className="mt-1 text-muted-ink text-sm">
                Registered in West Bengal, India · Incorporated {COMPANY.founded}
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* MVV */}
      <section className="py-24 bg-cream-2">
        <div className="container mx-auto px-6 md:px-10 xl:px-16">
          <FadeUp className="max-w-2xl mb-16">
            <p className="eyebrow text-forest">Principles</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-forest leading-tight">
              The compass behind every harvest.
            </h2>
          </FadeUp>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {MVV.map((m, i) => (
              <StaggerItem key={m.label}>
                <div data-testid={`about-mvv-${i}`} className="bg-cream p-8 h-full border border-border-soft">
                  <p className="eyebrow text-saffron">0{i + 1} — {m.label}</p>
                  <h3 className="mt-4 font-serif text-2xl md:text-3xl text-forest">{m.title}</h3>
                  <p className="mt-4 text-muted-ink">{m.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Why */}
      <section className="py-24 bg-cream">
        <div className="container mx-auto px-6 md:px-10 xl:px-16">
          <FadeUp className="max-w-2xl mb-14">
            <p className="eyebrow text-forest">What Sets Us Apart</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest">
              Four advantages compounding every season.
            </h2>
          </FadeUp>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = iconMap[w.icon] || Leaf;
              return (
                <StaggerItem key={w.title}>
                  <div className="p-8 h-full border border-border-soft bg-cream-2/40">
                    <Icon size={28} className="text-saffron" strokeWidth={1.4} />
                    <h3 className="mt-6 font-serif text-xl text-forest">{w.title}</h3>
                    <p className="mt-3 text-muted-ink text-sm">{w.text}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-forest text-cream relative overflow-hidden">
        <div className="grain" />
        <div className="container mx-auto px-6 md:px-10 xl:px-16 relative">
          <FadeUp className="max-w-2xl mb-14">
            <p className="eyebrow text-saffron">People</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">
              A small team, obsessed with doing this right.
            </h2>
          </FadeUp>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 max-w-5xl">
            {TEAM.map((t, i) => (
              <StaggerItem key={t.name}>
                <div data-testid={`team-${i}`} className="group">
                  <div className="aspect-[4/5] overflow-hidden bg-cream/10">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-5">
                    <p className="eyebrow text-saffron">{t.role}</p>
                    <h3 className="mt-2 font-serif text-2xl">{t.name}</h3>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  </>
);

export default About;
